import { lazy } from 'react';
import type { ReactElement } from 'react';
import type { RouteObject } from 'react-router-dom';
import { RequireFeature } from '@/features/authentication';
import { RFQ_FEATURE } from '@/features/bids';
import { LazyLoad } from '@/shared/components/LazyLoad';
import { SupplierBidsGate } from './access/components/SupplierBidsGate';
import { SUPPLIER_BID_FEATURE } from './common/models/supplierQuotation.constants';

const SupplierBidListPage = lazy(() => import('./bids-list/pages/SupplierBidListPage'));
const SupplierBidDetailPage = lazy(() => import('./bid-detail/pages/SupplierBidDetailPage'));
const SupplierBidPage = lazy(() => import('./quotation-form/pages/SupplierBidPage'));

/**
 * A supplier BID screen: behind BIDs in RBAC (the Supplier role's BIDs permission, 2026-10-27) and behind Supplier Bid,
 * the feature its endpoints are guarded by.
 */
function supplierGuarded(element: ReactElement): RouteObject['element'] {
  return (
    <RequireFeature feature={RFQ_FEATURE}>
      <RequireFeature feature={SUPPLIER_BID_FEATURE}>
        <LazyLoad>{element}</LazyLoad>
      </RequireFeature>
    </RequireFeature>
  );
}

/**
 * The Supplier Bid Quotation (2026-10-23): the BIDs a supplier is invited to, one BID's details, and its quotation.
 * Relative paths: `app/routes/protectedRoutes.tsx` nests these inside the shell. Open to the Supplier role alone: any
 * other role is sent to the buyer's BIDs.
 */
export const supplierQuotationRoutes: RouteObject[] = [
  {
    path: 'supplier-bids',
    element: <SupplierBidsGate />,
    children: [
      { index: true, element: supplierGuarded(<SupplierBidListPage />) },
      { path: ':rfqId', element: supplierGuarded(<SupplierBidDetailPage />) },
      { path: ':rfqId/quotation', element: supplierGuarded(<SupplierBidPage />) },
    ],
  },
];
